import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { SetupPreview, RoleItem, AttendanceConfiguration } from './attendance.types';

@Component({
  selector: 'app-attendance-setup-form', standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule],
  templateUrl: './attendance-setup-form.component.html',
  styleUrls: ['./attendance-common.css', './attendance-setup-form.component.css'],
})
export class AttendanceSetupFormComponent {
  @Input({required:true}) setup!: SetupPreview;
  @Input() showSpinner = false;
  @Input() setupOpened = false;
  @Input() setupChanged = false;
  @Input() answerDate = '';
  @Input() answerTime = '';
  @Output() answerDateChange = new EventEmitter<string>();
  @Output() answerTimeChange = new EventEmitter<string>();
  @Output() changed = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();
  organizationSearch = ''; roleSearch = '';

  changeQuorum(): void {
    this.changed.emit();
    if (this.setup?.settings.quorumMode === 'none') {
      this.setup.settings.quorumTotal = null;
      this.setup.settings.quorumRequired = null;
      this.setup.settings.quorumRoleIds = [];
    } else if (this.setup && !this.setup.settings.quorumRoleIds.length) {
      this.setup.settings.quorumRoleIds = this.setup.roles.map(r => r.id);
    }
  }

  toggleQuorumRole(id: number, checked: boolean): void {
    if (!this.setup) return;
    this.changed.emit();
    this.setup.settings.quorumRoleIds = checked
      ? [...new Set([...this.setup.settings.quorumRoleIds, id])]
      : this.setup.settings.quorumRoleIds.filter(r => r !== id);
  }

  get wholeOrganization(): boolean { return !!this.setup?.configuration.condition.target_org_ids.includes(-1); }

  get filteredOrganizations(): RoleItem[] { return this.setup?.organizationOptions.filter(x => x.name.includes(this.organizationSearch)) || []; }

  get filteredRoles(): RoleItem[] { return this.setup?.roleOptions.filter(x => x.name.includes(this.roleSearch)) || []; }

  get quorumNeeded(): number | null {
    const s = this.setup?.settings;
    if (!s?.quorumTotal || s.quorumTotal < 1) return null;
    return s.quorumMode === 'majority' ? Math.floor(s.quorumTotal / 2) + 1 : s.quorumRequired;
  }

  get quorumRoleNames(): string { return this.setup?.roles.filter(r => this.setup!.settings.quorumRoleIds.includes(r.id)).map(r => r.name).join('・') || '役職を選択してください'; }

  toggleCondition(key: 'target_org_ids' | 'role_ids', id: number, checked: boolean): void {
    if (!this.setup || this.setupOpened) return;
    const values=this.setup.configuration.condition[key];
    if (key === 'target_org_ids' && id === -1) {
      this.setup.configuration.condition.target_org_ids = checked ? [-1] : [];
    } else {
      if (key === 'target_org_ids' && this.wholeOrganization) return;
      this.setup.configuration.condition[key]=checked ? [...new Set([...values,id])] : values.filter(v => v!==id);
    }
    this.setup.roles=this.setup.roleOptions.filter(r => this.setup!.configuration.condition.role_ids.includes(r.id));
    this.setup.settings.quorumRoleIds=this.setup.settings.quorumRoleIds.filter(id => this.setup!.configuration.condition.role_ids.includes(id));
    this.changed.emit();
  }

  addSession(): void {
    if (!this.setup) return;
    const previous=this.setup.configuration.sessions.at(-1);
    const time=(key: 'start'|'end'|'checkinStart') => `T${previous?.[key].split('T')[1] || ''}`;
    this.setup.configuration.sessions.push({start:time('start'),end:time('end'),checkinStart:time('checkinStart')});
    this.clearMultiDayQuorum();
    this.changed.emit();
  }

  usePlanSchedule(): void {
    if (!this.setup) return;
    this.setup.configuration.sessions=this.setup.planSessions.map(s=>({...s}));
    this.clearMultiDayQuorum();
    this.setup.scheduleNotice=this.setup.planScheduleNotice;
    this.changed.emit();
  }

  setSessionDate(session: AttendanceConfiguration['sessions'][number], value: string): void {
    for (const key of ['start','end','checkinStart'] as const) session[key]=`${value}T${session[key].split('T')[1] || ''}`;
    this.changed.emit();
  }

  setSessionPart(session: AttendanceConfiguration['sessions'][number], key: 'start'|'end'|'checkinStart', part: 'date'|'time', value: string): void {
    const [date='',time='']=session[key].split('T');
    session[key]=part==='date' ? `${value}T${time || '09:00'}` : `${date}T${value}`;
    this.changed.emit();
  }

  private clearMultiDayQuorum(): void {
    if (this.setup && this.setup.configuration.sessions.length > 1) {
      Object.assign(this.setup.settings, {quorumMode:'none',quorumTotal:null,quorumRequired:null,quorumRoleIds:[]});
    }
  }
}
